import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Truck, ArrowRight, ChevronRight, Phone, Calculator as CalculatorIcon, CheckCircle2, ReceiptText } from 'lucide-react';
import { SERVICE_PRODUCT_SLUGS, getServiceProduct } from '../data/cityMaterialContent';
import { SERVICE_COUNTIES, getServiceCounty, getCitiesInCounty } from '../data/counties';
import { JsonLd, useSEO } from '../utils/seo';
import { NotFound } from './NotFound';

// Same three-ring tier mapping the city pages use: inner (zones 1+2 → +$80),
// middle (zone 3 → +$120), outer (zones 4+5 → +$175).
const ZONE_FEE: Record<number, string> = {
  1: '+$80',
  2: '+$80',
  3: '+$120',
  4: '+$175',
  5: '+$175'
};
// Legacy curated zone (1–5) → the tier number customers actually see. There
// are only three zones, so a city stored as 4 or 5 must display as Zone 3.
const ZONE_TIER: Record<number, 1 | 2 | 3> = {
  1: 1,
  2: 1,
  3: 2,
  4: 3,
  5: 3
};
// The three real delivery tiers, lowest to highest.
const TIER_FEES = ['+$80', '+$120', '+$175'];
export function County() {
  const {
    countySlug = ''
  } = useParams<{
    countySlug: string;
  }>();
  const county = getServiceCounty(countySlug);
  useSEO({
    title: county ? `Gravel & Aggregate Delivery in ${county.name}, WA | 206Gravel` : 'Gravel Delivery Service Areas | 206Gravel',
    description: county ? county.blurb : 'Browse bulk gravel, rock, soil, bark, and aggregate delivery across Seattle, King County, and Pierce County.',
    path: county ? `/gravel-delivery/${county.slug}` : '/service-areas'
  });
  if (!county) return <NotFound data-id="element-2367" data-mp="2asv9z7d4fznk" />;
  const cities = getCitiesInCounty(county);
  const materials = SERVICE_PRODUCT_SLUGS.map(slug => getServiceProduct(slug)).filter(Boolean) as {
    product: any;
    category: any;
  }[];
  const path = `/gravel-delivery/${county.slug}`;
  const lowestPrice = materials.length ? Math.min(...materials.map(m => m.product.pricePerYard)) : 0;
  const otherCounties = SERVICE_COUNTIES.filter(c => c.slug !== county.slug);
  const faqs = [{
    q: `Do you charge for delivery in ${county.name}?`,
    a: `Yes — and we show it as its own line rather than folding it into the per-yard price. Delivery runs from ${TIER_FEES[0]} to ${TIER_FEES[2]} per load across three zones depending on how far the drop site is from the pit. "Free delivery" offers still pay for the truck; the haul is just built into the yard price, which makes the material look cheaper than it is. Our estimator shows the all-in delivered total before you order, so you can compare one number against one number.`
  }, {
    q: `How fast can you deliver in ${county.name}?`,
    a: `${county.leadTime}. Later orders typically go out next business day. We run our own trucks and drivers, so scheduling doesn't depend on a third-party hauler being available.`
  }, {
    q: `What's the minimum order for ${county.name} delivery?`,
    a: `Three cubic yards. Loads under three yards use a flat $250 micro-load delivery fee, so it's usually worth rounding up to a full load if your project can use the material.`
  }, {
    q: `Which cities in ${county.name} do you deliver to?`,
    a: `We publish delivery pages for ${cities.map(c => c.name).join(', ')} and deliver to the surrounding communities as well. If you don't see your city, enter your address in the estimator and it will return a zone and fee or tell you to call for a custom quote.`
  }];
  return <main className="bg-[#fdfbf7] dark:bg-[#222222] min-h-screen" data-id="element-2368" data-mp="3anziybgu91rj">
      <JsonLd data={[{
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'Bulk gravel and aggregate delivery',
      name: `Gravel & aggregate delivery in ${county.name}, ${county.state}`,
      description: county.blurb,
      provider: {
        '@type': 'LocalBusiness',
        name: '206Gravel',
        telephone: '+1-206-619-3742',
        url: 'https://206gravel.com/'
      },
      areaServed: {
        '@type': 'AdministrativeArea',
        name: `${county.name}, ${county.state}`
      },
      url: `https://206gravel.com${path}`,
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'USD',
        lowPrice: lowestPrice,
        offerCount: materials.length,
        availability: 'https://schema.org/InStock'
      }
    }, {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://206gravel.com/'
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'Service Areas',
        item: 'https://206gravel.com/service-areas'
      }, {
        '@type': 'ListItem',
        position: 3,
        name: county.name,
        item: `https://206gravel.com${path}`
      }]
    }, {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(f => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: f.a
        }
      }))
    }]} data-id="element-2369" data-mp="3elnhdohqpco6" />

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6" data-id="element-2370" data-mp="28w9ljvk093iz">
        <ol className="flex items-center gap-1.5 text-xs text-stone-500 dark:text-stone-400" data-id="element-2371" data-mp="1u7d63dfmco8c">
          <li data-id="element-2372" data-mp="1z4lsjq7svefe">
            <Link to="/" className="hover:text-brand-orange transition-colors" data-id="element-2373" data-mp="1z66kr96n6la3">
              Home
            </Link>
          </li>
          <ChevronRight className="w-3 h-3" data-id="element-2374" data-mp="2mswihybyjtow" />
          <li data-id="element-2375" data-mp="2ruc69y5iseaz">
            <Link to="/service-areas" className="hover:text-brand-orange transition-colors" data-id="element-2376" data-mp="2rsrbq8j7gvd4">
              Service Areas
            </Link>
          </li>
          <ChevronRight className="w-3 h-3" data-id="element-2377" data-mp="2hvwcfnf3yqxs" />
          <li className="text-stone-900 dark:text-white font-semibold truncate" aria-current="page" data-id="element-2378" data-mp="2hula7s1b9r8s">
            {county.name}
          </li>
        </ol>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12" data-id="element-2379" data-mp="16gzko5rjhnmq">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="max-w-3xl" data-id="element-2380" data-mp="3934wysm3byss">
          <div className="inline-flex items-center gap-2 mb-3" data-id="element-2381" data-mp="2zdbqu74pwbvx">
            <MapPin className="w-3.5 h-3.5 text-brand-orange" data-id="element-2382" data-mp="2zbifwfn2n5i4" />
            <span className="text-[11px] font-bold text-brand-orange uppercase tracking-[0.14em]" data-id="element-2383" data-mp="2udh0c4outobe">
              {county.name}, {county.state} · {cities.length} cities served
            </span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-stone-900 dark:text-white leading-[1.05]" data-id="element-2384" data-mp="2ld0z71vl2t9p">
            Gravel &amp; Aggregate Delivery in {county.name},{' '}
            {county.state}
          </h1>
          <p className="mt-4 text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2385" data-mp="en44u2ftcrgj">
            {county.intro} We cover {county.landmarks.slice(0, -1).join(', ')},
            and {county.landmarks[county.landmarks.length - 1]}.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-2386" data-mp="h70qz9tjdh2h">
            <Link to="/#estimator" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-brand-orange hover:bg-brand-orange-hover text-white font-bold text-sm transition-colors shadow-lg shadow-brand-orange/20" data-id="element-2387" data-mp="m6dd1scl2k40">
              <CalculatorIcon className="w-4 h-4" data-id="element-2388" data-mp="vvdhhr9uu7pl" />
              Get a Delivered Price
              <ArrowRight className="w-4 h-4" data-id="element-2389" data-mp="3ufdoywpoe7tm" />
            </Link>
            <a href="tel:206-619-3742" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-stone-300 dark:border-white/15 text-stone-900 dark:text-white font-bold text-sm hover:bg-stone-100 dark:hover:bg-white/5 transition-colors" data-id="element-2390" data-mp="3811y124305s">
              <Phone className="w-4 h-4" data-id="element-2391" data-mp="d7ry076bln7z" />
              (206) 619-3742
            </a>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-3 text-xs" data-id="element-2392" data-mp="1jlzpjijuzrzl">
            <div className="px-3 py-2.5 rounded-lg bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2393" data-mp="1okkn1a7q7xkc">
              <div className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-stone-400 font-bold mb-0.5" data-id="element-2394" data-mp="29py1o4zfn4st">
                Materials from
              </div>
              <div className="font-bold text-stone-900 dark:text-white" data-id="element-2395" data-mp="2jg9ekh13gobu">
                ${lowestPrice}/yd
              </div>
            </div>
            <div className="px-3 py-2.5 rounded-lg bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2396" data-mp="2ememoqbc531o">
              <div className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-stone-400 font-bold mb-0.5" data-id="element-2397" data-mp="2oekmgm7tworf">
                Delivery
              </div>
              <div className="font-bold text-stone-900 dark:text-white" data-id="element-2398" data-mp="32bzhc6gupoqs">
{TIER_FEES[0]}–{TIER_FEES[2]}
              </div>
            </div>
            <div className="px-3 py-2.5 rounded-lg bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2399" data-mp="17zj4unxgpedh">
              <div className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-stone-400 font-bold mb-0.5" data-id="element-2400" data-mp="1329g7ahwd6qp">
                Lead Time
              </div>
              <div className="font-bold text-stone-900 dark:text-white" data-id="element-2401" data-mp="3jnxq3w7mvaut">
                Same-day*
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Delivered-price transparency. This is the counter to "free delivery"
          marketing: state plainly that the haul is never free, and that we
          quote the all-in number instead of hiding it in the yard price. */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-stone-200 dark:border-white/10" data-id="element-2402" data-mp="30xy3dfz9m7uk">
        <h2 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-2403" data-mp="2m174d7ti6y5h">
          One delivered price, before you order
        </h2>
        <p className="mt-3 max-w-3xl text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2404" data-mp="k8143zmdhsgq">
          Nobody hauls rock for free. When a site advertises “free
          delivery,” the truck is paid for inside the per-yard price — which
          makes the material look cheaper and the real cost harder to check.
          We quote the material and the haul as separate lines, then show you
          the all-in delivered total for your address. That's the number worth
          comparing.
        </p>
        <div className="mt-6 grid sm:grid-cols-3 gap-4" data-id="element-2405" data-mp="1pcf0vtiax0vx">
          {[{
          icon: ReceiptText,
          title: 'Delivered total up front',
          body: 'Enter an address and yardage and the estimator returns the full price including the haul — no callback required.'
        }, {
          icon: Truck,
          title: 'Our trucks, our drivers',
          body: `We're the hauler, not a marketplace routing your order to whoever bids lowest. The driver who shows up works for us.`
        }, {
          icon: CheckCircle2,
          title: 'Pay on delivery',
          body: 'No card at checkout. Cash, credit or debit card, Apple Pay, or Google Pay when the load arrives.'
        }].map(item => <div key={item.title} className="p-6 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2406" data-mp="3avinywh2q6ch">
              <item.icon className="w-5 h-5 text-brand-orange" data-id="element-2407" data-mp="35wz2syab0yl6" />
              <h3 className="mt-3 font-heading text-base font-bold text-stone-900 dark:text-white" data-id="element-2408" data-mp="3jvyobx2isxd4">
                {item.title}
              </h3>
              <p className="mt-1.5 text-sm text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2409" data-mp="3ftk7fiiew7oh">
                {item.body}
              </p>
            </div>)}
        </div>
      </section>

      {/* Materials */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-stone-200 dark:border-white/10" data-id="element-2410" data-mp="30y4931astpma">
        <h2 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-2411" data-mp="2w6vxpzpwru6h">
          Materials we deliver in {county.name}
        </h2>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2412" data-mp="28fgsnz6qyyrj">
          {materials.map(({
          product
        }) => <Link key={product.slug} to={`/shop/${product.slug}`} className="group rounded-2xl overflow-hidden bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 hover:border-brand-orange transition-colors" data-id="element-2413" data-mp="7g55tkovapdt">
              <img src={product.image} alt={product.name} className="w-full h-32 object-cover" loading="lazy" data-id="element-2414" data-mp="h6pyyslrv1pc" />
              <div className="p-4" data-id="element-2415" data-mp="m3yhwveqv9rn">
                <h3 className="font-heading text-base font-bold text-stone-900 dark:text-white group-hover:text-brand-orange transition-colors" data-id="element-2416" data-mp="3km5a5x5vzhc2">
                  {product.name}
                </h3>
                <p className="mt-1 text-xs text-stone-500 dark:text-stone-400" data-id="element-2417" data-mp="3ukb9c70b69d8">
                  ${product.pricePerYard}/yd · delivered countywide
                </p>
              </div>
            </Link>)}
        </div>
      </section>

      {/* Cities */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-stone-200 dark:border-white/10" data-id="element-2418" data-mp="19tki9m6urv18">
        <h2 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-2419" data-mp="1ev9uv69q4grf">
          {county.name} cities we deliver to
        </h2>
        <p className="mt-3 max-w-3xl text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2420" data-mp="2udfvz4enx40s">
          Closest zones first — the lower the zone, the lower the per-load
          delivery fee.
        </p>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2421" data-mp="2kliifr06lzyw">
          {cities.map(city => <div key={city.slug} className="p-5 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2422" data-mp="2fo9wrgftrdsw">
              <div className="flex items-baseline justify-between gap-3" data-id="element-2423" data-mp="2bnq3wp2aawck">
                <h3 className="font-heading text-base font-bold text-stone-900 dark:text-white" data-id="element-2424" data-mp="26n2da4hywhiv">
                  {city.name}
                </h3>
                <span className="shrink-0 text-[11px] font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400" data-id="element-2425" data-mp="4uxec921wjyn">
                  Zone {ZONE_TIER[city.zone]} · {ZONE_FEE[city.zone]}
                </span>
              </div>
              <ul className="mt-3 space-y-1.5" data-id="element-2426" data-mp="2tpdp9qbd0lnr">
                {materials.map(({
              product
            }) => <li key={product.slug} data-id="element-2427" data-mp="2xocnks2l5k68">
                    <Link to={`/gravel-delivery/${city.slug}/${product.slug}`} className="group inline-flex items-center gap-1.5 text-sm text-stone-600 dark:text-stone-300 hover:text-brand-orange transition-colors" data-id="element-2428" data-mp="2jpv81hmu3dze">
                      {product.name} in {city.name}
                      <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2429" data-mp="29ys5sci64syj" />
                    </Link>
                  </li>)}
              </ul>
            </div>)}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-stone-200 dark:border-white/10" data-id="element-2430" data-mp="25y89fb5f5tdg">
        <h2 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-2431" data-mp="1r0xqjgqg5l7p">
          {county.name} delivery questions
        </h2>
        <div className="mt-6 grid md:grid-cols-2 gap-4" data-id="element-2432" data-mp="1w250nmg2awhj">
          {faqs.map(faq => <div key={faq.q} className="p-6 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2433" data-mp="1ces9cwprbyfv">
              <h3 className="font-heading text-base font-bold text-stone-900 dark:text-white" data-id="element-2434" data-mp="1mces5ua6ga54">
                {faq.q}
              </h3>
              <p className="mt-2 text-sm text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2435" data-mp="1hck4s0pm22ot">
                {faq.a}
              </p>
            </div>)}
        </div>
      </section>

      {/* Other counties + final CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-stone-200 dark:border-white/10" data-id="element-2436" data-mp="1rbj0kipa23ue">
        {otherCounties.length > 0 && <div className="mb-8 flex flex-wrap items-center gap-3" data-id="element-2437" data-mp="203kzgcjp7c2k">
            <span className="text-xs font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400" data-id="element-2438" data-mp="206fiq0qz6que">
              Also serving
            </span>
            {otherCounties.map(c => <Link key={c.slug} to={`/gravel-delivery/${c.slug}`} className="inline-flex items-center gap-1.5 text-sm font-bold text-stone-800 dark:text-white hover:text-brand-orange transition-colors" data-id="element-2439" data-mp="2a43qpsdlduui">
                {c.name}
                <ArrowRight className="w-3.5 h-3.5" data-id="element-2440" data-mp="2r3n2tf0aqoxm" />
              </Link>)}
          </div>}
        <div className="p-8 rounded-2xl bg-stone-900 dark:bg-black/40 border border-stone-800 dark:border-white/10" data-id="element-2441" data-mp="2r1tt1qudzole">
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-white tracking-tight" data-id="element-2442" data-mp="30tq3809qz55z">
            Get your {county.name} delivered price in about a minute
          </h2>
          <p className="mt-3 max-w-2xl text-stone-300 leading-relaxed" data-id="element-2443" data-mp="34ww4eti0665l">
            Enter your address, pick a material and yardage, and see the all-in
            total — material plus haul — before you commit to anything.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-2444" data-mp="1fgwomtjvpigz">
            <Link to="/#estimator" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-brand-orange hover:bg-brand-orange-hover text-white font-bold text-sm transition-colors" data-id="element-2445" data-mp="1kf6pnksabdai">
              <CalculatorIcon className="w-4 h-4" data-id="element-2446" data-mp="184yvofrrbuce" />
              Get a Delivered Price
              <ArrowRight className="w-4 h-4" data-id="element-2447" data-mp="182vz1cw3n2yp" />
            </Link>
            <a href="tel:206-619-3742" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-white/20 text-white font-bold text-sm hover:bg-white/10 transition-colors" data-id="element-2448" data-mp="136nmm79adnv8">
              <Phone className="w-4 h-4" data-id="element-2449" data-mp="1vt8cf3a45rwo" />
              (206) 619-3742
            </a>
          </div>
          <p className="mt-5 text-[11px] text-stone-400" data-id="element-2450" data-mp="1qwrpgcenhjl6">
            *Same-day delivery subject to truck availability and order cutoff
            times.
          </p>
        </div>
      </section>
    </main>;
}